import { z } from 'zod'

import { apiValueSchema, periodResponseSchema, valueStatusSchema } from './common'

/** Очередь у одной кассы сейчас — internal_api.CashDeskQueueResponse. */
export const cashDeskQueueSchema = z.object({
  cash_desk_number: z.number(),
  people: apiValueSchema,
})

/**
 * GET /queues — internal_api.QueuesResponse (ТЗ п. 5.1): люди в очереди сейчас по кассовой зоне и по
 * кассам. Период не учитывает; показание старше QUEUE_STATUS_STALE_AFTER деградирует в unavailable.
 * measured_cash_desks/cash_desk_count говорят, какую часть зоны описывает total: ноль по одной
 * кассе из двух — не ноль по всей зоне.
 */
export const queuesSchema = z.object({
  total: apiValueSchema,
  measured_cash_desks: z.number(),
  cash_desk_count: z.number(),
  cash_desks: z.array(cashDeskQueueSchema),
  updated_at: z.string().nullable().optional(),
})
export type Queues = z.infer<typeof queuesSchema>

/**
 * Среднее за период — internal_api.QueueAverageResponse. С 30.09.2026 average_people целое и есть только
 * при ok и среднем от одного человека; меньше одного — average_people: null и less_than_one: true
 * (нуля бэкенд не шлёт: бизнес читает его как «очередей не было»).
 */
export const queueAverageSchema = z.object({
  status: valueStatusSchema,
  average_people: z.number().nullable().optional(),
  less_than_one: z.boolean().optional(),
})
export type QueueAverage = z.infer<typeof queueAverageSchema>

/**
 * Средняя очередь для плитки (ТЗ п. 5.2): целое число людей или «<1»; undefined — данных за период нет.
 * Дробное average_people от бэкенда до 30.09.2026 приводим к тому же виду, чтобы порядок деплоя не важен.
 */
export function queueAverageText(average: QueueAverage | undefined): string | undefined {
  if (average?.status !== 'ok') return undefined
  if (average.less_than_one) return '<1'
  const people = average.average_people
  if (people == null) return undefined
  return people < 1 ? '<1' : String(Math.round(people))
}

/** Точка графика 5.4 — internal_api.QueueZoneBucketResponse / QueueHistoryBucketResponse. */
export const queueBucketSchema = z.object({
  interval_start: z.string(),
  status: valueStatusSchema,
  average_people: z.number().nullable().optional(),
  measured_cash_desks: z.number().optional(),
  cash_desk_count: z.number().optional(),
})
export type QueueBucket = z.infer<typeof queueBucketSchema>

/** Тот же период по одной кассе — internal_api.QueueHistoryResponse (для таблицы «Кассы», 5.4 его не рисует). */
export const queueDeskHistorySchema = z.object({
  cash_desk_number: z.number(),
  status: valueStatusSchema,
  average: queueAverageSchema,
  buckets: z.array(queueBucketSchema),
})

/** GET /queues/history?period|from&to — internal_api.QueuesHistoryResponse (ТЗ п. 5.2, 5.4). Бакеты плотные: неизмеренный — no_data. */
export const queuesHistorySchema = z.object({
  period: periodResponseSchema,
  average: queueAverageSchema,
  measured_cash_desks: z.number(),
  cash_desk_count: z.number(),
  buckets: z.array(queueBucketSchema),
  cash_desks: z.array(queueDeskHistorySchema),
})
export type QueuesHistory = z.infer<typeof queuesHistorySchema>
